
import React, { useState, useEffect, useRef } from 'react';
import { useAppContext } from '../../AppContext';
import { collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, limit, where, Timestamp } from 'firebase/firestore';
import LoadingSpinner from '../LoadingSpinner';
import * as LucideIcons from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ShowdownChat = ({ nexusId }) => {
    const { t } = useTranslation();
    const { db, user } = useAppContext();
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const messagesEndRef = useRef(null);

    // Session start timestamp to filter old history
    const sessionStart = useRef(Timestamp.now());

    const chatPath = nexusId 
        ? `artifacts/whispers-of-harmony/public/chats/${nexusId}` 
        : `artifacts/whispers-of-harmony/public/chats/global`;

    useEffect(() => {
        if (!db) {
            setLoading(false);
            return;
        }

        const chatCollection = collection(db, chatPath);
        // Query only messages created after this session started
        const q = query(
            chatCollection, 
            where("timestamp", ">=", sessionStart.current),
            orderBy("timestamp", "asc")
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const msgs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setMessages(msgs);
            setLoading(false);
        }, (err) => {
            console.error("Chat Error:", err);
            setError(err);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [db, chatPath]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (newMessage.trim() === "" || !user) return;

        const chatCollection = collection(db, chatPath);
        await addDoc(chatCollection, {
            text: newMessage,
            timestamp: serverTimestamp(),
            userId: user.uid,
            displayName: user.displayName,
            photoURL: user.photoURL,
        });

        setNewMessage("");
    };

    if (loading) return <LoadingSpinner message={t('showdown.showdownChat.loading')} />;
    if (error) return <p className="text-red-500">{t('showdown.showdownChat.error')}</p>;

    return (
        <div className="flex flex-col h-full bg-gray-900/50 rounded-lg border border-cyan-500/20">
            <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-4">
                {messages.map((msg) => {
                    const isUser = msg.userId === user.uid;
                    return (
                        <div key={msg.id} className={`flex items-end gap-2 ${isUser ? 'flex-row-reverse' : ''}`}>
                            <img src={msg.photoURL || "https://placehold.co/32x32/AEC6CF/FFFFFF?text=U"} alt={msg.displayName} className="w-8 h-8 rounded-full object-cover" />
                            <div className={`max-w-[70%] p-3 rounded-2xl ${isUser ? 'bg-blue-600 text-white rounded-br-md' : 'bg-gray-700 text-gray-100 rounded-bl-md'}`}>
                                {!isUser && <p className="text-xs font-bold text-cyan-300 mb-1">{msg.displayName}</p>}
                                <p className="text-sm leading-relaxed break-words">{msg.text}</p>
                            </div>
                        </div>
                    );
                })}
                <div ref={messagesEndRef} />
            </div>
            <div className="p-2 border-t border-gray-700/50">
                <form onSubmit={handleSendMessage} className="flex items-center gap-2">
                    <input type="text" value={newMessage} onChange={e => setNewMessage(e.target.value)} placeholder={t('showdown.showdownChat.placeholder')} className="flex-1 bg-gray-800 rounded-full py-2 px-4 text-white outline-none focus:ring-2 focus:ring-blue-500" />
                    <button type="submit" className="p-2 rounded-full text-purple-400 hover:bg-purple-500/20 disabled:opacity-50 transition-colors"><LucideIcons.Send size={20} /></button>
                </form>
            </div>
        </div>
    );
};

export default ShowdownChat;
